import * as pdfjsLib from "pdfjs-dist";
import dayjs from "dayjs";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import { collection, getDocs, query, where } from "firebase/firestore";
import { db } from "../../firebase";


dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

export const normalizePdfUrl = (value) => {
  if (!value) return "";
  if (/^https?:\/\//i.test(value)) return value;
  const path = value.startsWith("/") ? value : `/${value}`;
  const normalized = path.startsWith("/pdfs/") ? path : `/pdfs${path}`;
  return `${window.location.origin}${normalized}`;
};

export const normalizeDashes = (value) => String(value || "").replace(/[\u2010-\u2015\u2212]/g, "-");

export const extractPageText = async (page) => {
  const textContent = await page.getTextContent();
  if (!textContent || !Array.isArray(textContent.items)) return "";
  return textContent.items.map((item) => item.str || "").join("\n");
};

const parseSeiteCounter = (pageText) => {
  const match = pageText.match(/Seite\s+(\d+)\s+von\s+(\d+)/i);
  if (!match) return null;
  return {
    current: parseInt(match[1], 10),
    total: parseInt(match[2], 10)
  };
};

const matchesDienstNummer = (pageText, number) => {
  const normalized = normalizeDashes(pageText);
  const regex = /Dienst-Nr\.\s*:\s*326\s*(\d{4})/i;
  const match = normalized.match(regex);
  if (!match) return false;
  return match[1] === number;
};

// Helper to check if a date is a holiday (Feiertag) in Firestore
const isHoliday = async (datePicked) => {
  const isoDate = dayjs(datePicked).format("YYYY-MM-DD");
  const q = query(collection(db, "Feiertage"), where("date", "==", isoDate));
  const snapshot = await getDocs(q);
  return !snapshot.empty;
};

const matchesDayBeforeUmlauf = (pageText, datePicked, forceSF = false) => {
  const normalized = normalizeDashes(pageText);
  const umlaufIndex = normalized.search(/Umlauf/i);
  const beforeUmlauf = umlaufIndex >= 0 ? normalized.slice(0, umlaufIndex) : normalized;
  const dayOfWeek = dayjs(datePicked).day();

  // Helper: check if a range is present
  const hasMoFr = /Mo\s*-\s*Fr/i.test(beforeUmlauf);
  const hasMoDo = /Mo\s*-\s*Do/i.test(beforeUmlauf);
  const hasMoMi = /Mo\s*-\s*Mi/i.test(beforeUmlauf);

  // Helper: check for single days
  const hasMo = /\bMo\b/i.test(beforeUmlauf);
  const hasDi = /\bDi\b/i.test(beforeUmlauf);
  const hasMi = /\bMi\b/i.test(beforeUmlauf);
  const hasDo = /\bDo\b/i.test(beforeUmlauf);
  const hasFr = /\bFr\b/i.test(beforeUmlauf);
  const hasSa = /\bSa\b/i.test(beforeUmlauf);
  const hasSF = /\bSF\b/i.test(beforeUmlauf);

  if (forceSF) {
    return hasSF;
  }

  // Exclusion rules
  // If Mo-Fr, ignore single Mo, Di, Mi, Do, Fr
  // If Mo-Do, ignore single Mo, Di, Mi, Do
  // If Mo-Mi, ignore single Mo, Di, Mi

  // Monday (1)
  if (dayOfWeek === 1) {
    if (hasMoFr || hasMoDo || hasMoMi) return true;
    if (hasMo && !hasMoFr && !hasMoDo && !hasMoMi) return true;
    return false;
  }
  // Tuesday (2)
  if (dayOfWeek === 2) {
    if (hasMoFr || hasMoDo || hasMoMi) return true;
    if (hasDi && !hasMoFr && !hasMoDo && !hasMoMi) return true;
    return false;
  }
  // Wednesday (3)
  if (dayOfWeek === 3) {
    if (hasMoFr || hasMoDo || hasMoMi) return true;
    if (hasMi && !hasMoFr && !hasMoDo && !hasMoMi) return true;
    return false;
  }
  // Thursday (4)
  if (dayOfWeek === 4) {
    if (hasMoFr || hasMoDo) return true;
    if (hasDo && !hasMoFr && !hasMoDo) return true;
    return false;
  }
  // Friday (5)
  if (dayOfWeek === 5) {
    if (hasMoFr) return true;
    if (hasFr && !hasMoFr) return true;
    return false;
  }
  // Saturday (6)
  if (dayOfWeek === 6) {
    return hasSa;
  }
  // Sunday/holiday (0)
  return hasSF;
};

async function searchNumberInPdf(pdfUrl, number, renderPageToImage, datePicked) {
  const loadingTask = pdfjsLib.getDocument(pdfUrl);
  const pdf = await loadingTask.promise;
  const pageCache = new Map();

  // Check if the picked date is a holiday
  const holiday = await isHoliday(datePicked);

  const readPageText = async (pageNum) => {
    if (pageCache.has(pageNum)) return pageCache.get(pageNum);
    const page = await pdf.getPage(pageNum);
    const pageText = await extractPageText(page);
    const entry = { page, pageText };
    pageCache.set(pageNum, entry);
    return entry;
  };

  let selectedPageNum = null;
  let selectedCounter = null;

  for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
    const { pageText } = await readPageText(pageNum);
    if (!matchesDienstNummer(pageText, number)) continue;
    if (!matchesDayBeforeUmlauf(pageText, datePicked, holiday)) continue;

    selectedPageNum = pageNum;
    selectedCounter = parseSeiteCounter(pageText);
    break;
  }

  if (!selectedPageNum) return [];

  const pageNums = new Set([selectedPageNum]);

  if (selectedCounter && selectedCounter.total && selectedCounter.current) {
    let prevExpected = selectedCounter.current - 1;
    for (let pageNum = selectedPageNum - 1; pageNum >= 1; pageNum--) {
      const { pageText } = await readPageText(pageNum);
      const counter = parseSeiteCounter(pageText);
      if (!counter || counter.total !== selectedCounter.total) break;
      if (counter.current !== prevExpected) break;
      pageNums.add(pageNum);
      prevExpected -= 1;
      if (counter.current === 1) break;
    }

    let nextExpected = selectedCounter.current + 1;
    for (let pageNum = selectedPageNum + 1; pageNum <= pdf.numPages; pageNum++) {
      const { pageText } = await readPageText(pageNum);
      const counter = parseSeiteCounter(pageText);
      if (!counter || counter.total !== selectedCounter.total) break;
      if (counter.current !== nextExpected) break;
      pageNums.add(pageNum);
      nextExpected += 1;
      if (counter.current === selectedCounter.total) break;
    }
  }

  const sortedPageNums = Array.from(pageNums).sort((a, b) => a - b);
  const finalMatches = [];

  for (const pageNum of sortedPageNums) {
    const imageUrl = await renderPageToImage(pdf, pageNum);
    finalMatches.push({ pageNum, imageUrl, crewSwap: [] });
  }

  return finalMatches;
}

async function loadPlansFromFirestore() {
  const snapshot = await getDocs(collection(db, "Fahrplaene"));
  const entries = [];

  for (const docSnap of snapshot.docs) {
    const data = docSnap.data();
    const pdfSnap = await getDocs(collection(db, "Fahrplaene", docSnap.id, "pdfs"));
    const pdfs = pdfSnap.docs
      .map((pdfDoc, index) => ({
        id: pdfDoc.id,
        ...pdfDoc.data(),
        order: pdfDoc.data().order ?? index + 1,
        pdfUrl: normalizePdfUrl(pdfDoc.data().pdfUrl)
      }))
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

    entries.push({
      id: docSnap.id,
      dateStart: data.dateStart || "",
      dateEnd: data.dateEnd || "",
      pdfs
    });
  }

  return entries;
}

export async function handleSearch({
  number,
  setFoundPages,
  setLoading,
  renderPageToImage,
  datePicked,
  setMultipleBerechnung
}) {
  const dienstPrefix = String(number).replace(/\D/g, "").slice(0, 2);
  const datePickedJS = dayjs(datePicked).startOf("day");

  setLoading(true);
  setFoundPages([]);
  if (setMultipleBerechnung) setMultipleBerechnung(false);

  try {
    const entries = await loadPlansFromFirestore();
    const matchingPlan = entries.find((plan) => {
      if (!plan.dateStart || !plan.dateEnd) return false;
      const start = dayjs(plan.dateStart, "YYYY-MM-DD").startOf("day");
      const end = dayjs(plan.dateEnd, "YYYY-MM-DD").startOf("day");
      return datePickedJS.isSameOrAfter(start) && datePickedJS.isSameOrBefore(end);
    });

    if (!matchingPlan || !matchingPlan.pdfs || matchingPlan.pdfs.length === 0) {
      setFoundPages([]);
      return;
    }

    const matchingPdfs = matchingPlan.pdfs.filter((pdf) => {
      const dienstLinie = Array.isArray(pdf.dienstLinie) ? pdf.dienstLinie : [pdf.dienstLinie];
      const normalized = dienstLinie
        .map((item) => String(item || "").replace(/\D/g, "").slice(0, 2))
        .filter((item) => /^\d{2}$/.test(item));
      return normalized.includes(dienstPrefix);
    });

    const finalMatches = [];
    for (const pdf of matchingPdfs) {
      if (!pdf.pdfUrl) continue;
      const matches = await searchNumberInPdf(pdf.pdfUrl, number, renderPageToImage, datePicked);
      if (matches.length > 0) {
        finalMatches.push(...matches);
        break;
      }
    }

    setFoundPages(finalMatches);
  } catch (error) {
    console.error("Failed to load Fahrplaene:", error);
    setFoundPages([]);
  } finally {
    setLoading(false);
  }
}
